import React, {Dispatch, useEffect, useMemo, useState} from 'react';

import {format} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {getExtendedCert, getLegalPersons} from 'api';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Modal} from 'components/ui-kit/Modal';
import {Spinner} from 'components/ui-kit/Spinner';
import {useCryptoPro} from 'core/hooks/cryptoPro';
import {doLoginWithCert} from 'models/auth';
import {LegalPerson} from 'types';
import {CertData, CryptoProCert} from 'types/plugins/cadesPlugin';

import {CertMockModal} from '../CertMockModal';

import {
    CancelButton,
    ContentWrapper,
    ControlsWrapper,
    List,
    ListItem,
    ListWrapper,
    LoadingWrapper,
    SpinnerLayer,
} from './styled';

export function CertAuthModal({show, setShow}: {show: boolean; setShow: Dispatch<boolean>}) {
    const LOCALE_CERTS_PATH = 'pages.login.certs.';
    const LOCALE_ORG_PATH = 'pages.login.organizations.';
    const {t} = useTranslation();
    const cryptoPro = useCryptoPro();

    const [showMock, setShowMock] = useState(false);

    const [loadingCerts, setLoadingCerts] = useState(false);
    const [currentCert, setCurrentCert] = useState<CertData | null>(null);
    const [selectedCert, setSelectedCert] = useState<CryptoProCert | null>(null);
    const [certsList, setCertsList] = React.useState<CryptoProCert[]>([]);
    const getCertsList = async () => {
        try {
            setLoadingCerts(true);
            const raw: CryptoProCert[] | undefined = await cryptoPro.getCertificatesInfo();
            if (!raw) return;
            setCertsList(raw);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingCerts(false);
        }
    };

    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [currentOrganization, setCurrentOrganization] = useState<LegalPerson | null>(null);
    const [selectedOrganization, setSelectedOrganization] = useState<LegalPerson | null>(null);
    const [organizationsList, setOrganizationsList] = useState<LegalPerson[]>([]);
    const getOrganizationsList = async (cert: CryptoProCert) => {
        try {
            setLoadingOrganizations(true);
            const extendedCert = (await getExtendedCert(cert)).data;
            setCurrentCert(extendedCert);
            const legalPersons = (await getLegalPersons(extendedCert)).data;
            setOrganizationsList(legalPersons.organizations);
        } catch (error) {
            toast.error(error.message);
            console.error(error);
        } finally {
            setLoadingOrganizations(false);
        }
    };

    const currentStep = useMemo(() => {
        if (organizationsList.length) return 'organizations';
        return 'certs';
    }, [organizationsList]);

    useEffect(() => {
        getCertsList();
    }, []);

    const onSelectCert = () => {
        if (!selectedCert) return;
        getOrganizationsList(selectedCert);
    };

    const onSelectOrganization = () => {
        if (!selectedOrganization) return;
        setCurrentOrganization(selectedOrganization);
        onAuth(currentCert, selectedOrganization);
    };

    const onClear = () => {
        setSelectedCert(null);
        setCurrentCert(null);
        setSelectedOrganization(null);
        setCurrentOrganization(null);
    };

    const onClose = () => {
        setShow(false);
        onClear();
    };

    const onBack = () => {
        setOrganizationsList([]);
    };

    const onRefresh = () => {
        onClear();
        getCertsList();
    };

    const onAuth = async (cert: CertData | null, org: LegalPerson | null) => {
        try {
            if (!cert || !org || !org.id) throw new Error('Cert or organization is required');
            doLoginWithCert({cert, id: org.id});
        } catch (error) {
            toast.error(error.message);
            console.error(error);
        }
    };

    const Loading = () => (
        <LoadingWrapper>
            <p>{t(`${currentStep === 'certs' ? LOCALE_ORG_PATH : LOCALE_CERTS_PATH}load`)}</p>
            <CancelButton onClick={onClose}>{t(LOCALE_CERTS_PATH + 'cancel')}</CancelButton>
            <SpinnerLayer>
                <Spinner />
            </SpinnerLayer>
        </LoadingWrapper>
    );

    const CertsList = () => (
        <ListWrapper>
            <List>
                {certsList.map((cert: CryptoProCert) => (
                    <ListItem
                        selected={selectedCert?.thumbprint === cert.thumbprint}
                        onClick={() => setSelectedCert(cert)}
                        key={cert.thumbprint}
                    >
                        <div>
                            <span>{t(LOCALE_CERTS_PATH + 'issuer')}:</span> {cert.issuer_name}
                        </div>
                        <div>
                            <span>{t(LOCALE_CERTS_PATH + 'subject')}:</span> {cert.subject_name}
                        </div>
                        <div>
                            <span>{t(LOCALE_CERTS_PATH + 'valid')}: </span>
                            {`${format(new Date(cert.valid_from_date), 'dd.MM.yyyy')} - ${format(
                                new Date(cert.valid_to_date),
                                'dd.MM.yyyy',
                            )}`}
                        </div>
                    </ListItem>
                ))}
            </List>
            <ControlsWrapper>
                <Button variant={ButtonVariant.Outline} disabled={loadingCerts} onClick={onClose}>
                    {t(LOCALE_CERTS_PATH + 'cancel')}
                </Button>
                <Button disabled={loadingOrganizations} onClick={() => setShowMock(true)}>
                    Select mock
                </Button>
                <Button disabled={loadingOrganizations} onClick={onRefresh}>
                    {t(LOCALE_CERTS_PATH + 'refresh')}
                </Button>
                <Button disabled={!selectedCert} onClick={onSelectCert}>
                    {t(LOCALE_CERTS_PATH + 'select')}
                </Button>
            </ControlsWrapper>
        </ListWrapper>
    );

    const OrganizationsList = () => (
        <ListWrapper>
            <List>
                {organizationsList?.map((org: LegalPerson) => (
                    <ListItem
                        selected={selectedOrganization?.tin === org.tin}
                        onClick={() => setSelectedOrganization(org)}
                        key={org.tin}
                    >
                        <div>
                            <span>{t(LOCALE_ORG_PATH + 'name')}:</span> {org.company_name}
                        </div>
                        <div>
                            <span>{t(LOCALE_ORG_PATH + 'legal')}:</span> {org.legal_address}
                        </div>
                        <div>
                            <span>{t(LOCALE_ORG_PATH + 'tin')}:</span> {org.tin}
                        </div>
                    </ListItem>
                ))}
            </List>
            <ControlsWrapper>
                <Button
                    variant={ButtonVariant.Outline}
                    disabled={loadingOrganizations}
                    onClick={onClose}
                >
                    {t(LOCALE_ORG_PATH + 'cancel')}
                </Button>
                <Button disabled={loadingOrganizations} onClick={onBack}>
                    {t(LOCALE_ORG_PATH + 'back')}
                </Button>
                <Button disabled={!selectedOrganization} onClick={onSelectOrganization}>
                    {t(LOCALE_ORG_PATH + 'ok')}
                </Button>
            </ControlsWrapper>
        </ListWrapper>
    );

    const Content = () => {
        if (loadingCerts || loadingOrganizations) return <Loading />;
        return currentStep === 'certs' ? <CertsList /> : <OrganizationsList />;
    };

    return (
        <Modal
            title={t(`${currentStep === 'certs' ? LOCALE_CERTS_PATH : LOCALE_ORG_PATH}title`)}
            show={show}
            onClose={() => setShow(false)}
        >
            <CertMockModal
                show={showMock}
                setShow={setShowMock}
                addCert={(item) => setCertsList((items) => [...items, item])}
            />
            <ContentWrapper>
                <Content />
            </ContentWrapper>
        </Modal>
    );
}
